import DOMPurify from 'isomorphic-dompurify'

/**
 * Etiquetas permitidas para el HTML de origen CMS (editor). Cubre encabezados,
 * párrafos, énfasis, listas, enlaces, imágenes en línea y separadores; descarta
 * `<script>`, `<iframe>`, etc.
 */
const ALLOWED_TAGS = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'br', 'span', 'div',
  'strong', 'b', 'em', 'i', 'u', 's',
  'ul', 'ol', 'li',
  'a', 'img', 'blockquote', 'hr',
] as const

/**
 * Atributos permitidos. Se admite `style`/`class` porque el CMS inserta estilos
 * en línea y clases; DOMPurify neutraliza los valores peligrosos
 * (`expression()`, `url(javascript:)`, etc.). Los manejadores de eventos
 * (`onerror`, `onclick`…) nunca están permitidos.
 */
const ALLOWED_ATTR = [
  'href', 'title', 'alt', 'src', 'target', 'rel', 'style', 'class',
] as const

/**
 * Subconjunto "en línea" (contenido de frase, WHATWG "phrasing content") para
 * HTML del CMS que va a insertarse dentro de elementos que solo admiten ese
 * tipo de contenido — p. ej. un `<button>` (la cabecera de un acordeón): nada
 * de bloques (`p`, `div`, encabezados, listas, `blockquote`, `hr`) ni
 * contenido interactivo (`a`, `img` sin `alt` puede vaciar el nombre
 * accesible del botón) — ver auditoría de accesibilidad,
 * specs/035-faqs-module. Un `<a>`/`<img>` dentro de un `<button>` es HTML
 * inválido (WHATWG: el `<button>` prohíbe descendientes de contenido
 * interactivo) y produce un rol/nombre accesible inconsistente entre
 * navegadores y lectores de pantalla.
 */
const INLINE_ALLOWED_TAGS = ['strong', 'b', 'em', 'i', 'u', 's', 'span', 'br'] as const
const INLINE_ALLOWED_ATTR = ['style', 'class'] as const

/**
 * Nombre legible del destino de un enlace a partir de su URL: último segmento
 * de la ruta («/entradas/ticket-digital/» → «Ticket digital»); sin ruta, el
 * dominio. `null` si no hay nada aprovechable.
 */
function nameFromHref(href: string): string | null {
  try {
    const url = new URL(href, 'https://www.elcorteingles.es')
    const segment = url.pathname.split('/').filter(Boolean).at(-1)
    const words = segment
      ? decodeURIComponent(segment).replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ').trim()
      : url.hostname.replace(/^www\./, '')
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : null
  } catch {
    return null
  }
}

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName !== 'A') return

  // Fuerza `rel="noopener noreferrer"` en los enlaces del cuerpo que abren
  // pestaña nueva (defensa contra tabnabbing — Constitución, Principio VIII).
  // El hook se registra una sola vez al evaluarse el módulo (DOMPurify es un
  // singleton).
  if (node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer')
  }

  // Enlace cuyo único contenido son imágenes sin texto alternativo (p. ej. un
  // banner enlazado desde el editor con `alt=""`): se quedaría sin nombre y un
  // lector de pantalla solo anunciaría «enlace» (WCAG 2.4.4/1.1.1). Se le pone
  // a la imagen el nombre del destino — `title` del enlace o, si no, su URL.
  const images = [...node.querySelectorAll('img')]
  const hasName =
    Boolean(node.textContent?.trim()) || images.some((img) => img.getAttribute('alt')?.trim())
  if (images.length === 0 || hasName) return
  const name = node.getAttribute('title')?.trim() || nameFromHref(node.getAttribute('href') ?? '')
  if (name) images[0]?.setAttribute('alt', name)
})

/**
 * Sanea HTML de origen CMS y recorta el `\r\n`/espacios finales que el backend
 * añade al `body`. Ejecuta en servidor (los módulos son Server Components), por
 * lo que DOMPurify no entra en el bundle de cliente.
 *
 * El texto plano (sin etiquetas) pasa sin cambios. Reutilizable por cualquier
 * módulo que renderice HTML del editor.
 *
 * @param dirty HTML o texto plano potencialmente inseguro.
 * @param options.inline `true` restringe el saneado al subconjunto "en línea"
 *   (sin bloques ni contenido interactivo) — usar cuando el resultado se
 *   inserta dentro de un elemento que no admite contenido de bloque/enlaces
 *   propios, como la cabecera-`<button>` de un acordeón (ver `INLINE_ALLOWED_TAGS`).
 * @returns HTML seguro para `dangerouslySetInnerHTML`, sin salto final sobrante.
 */
export function sanitizeHtml(dirty: string, options?: { inline?: boolean }): string {
  const trimmed = dirty.trimEnd()
  if (!trimmed) return ''

  const tags = options?.inline ? INLINE_ALLOWED_TAGS : ALLOWED_TAGS
  const attrs = options?.inline ? INLINE_ALLOWED_ATTR : ALLOWED_ATTR

  return DOMPurify.sanitize(trimmed, {
    ALLOWED_TAGS: [...tags],
    ALLOWED_ATTR: [...attrs],
  })
}
